import type { SupplyKind } from "@frontier/content/supplies";
import type { ClientInput } from "@frontier/protocol/contracts";
import { normalizeMovement } from "./geometry";
import type { Point } from "./geometry";
import type { NavigationState, PointNavigation } from "./navigation-types";
import { abilityShortcutIndex } from "../ability-controls";
import { mouseFollowMovement } from "./mouse-steering";
import { neutralFlightInput } from "../flight-input";

const movementKeys = new Set([
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "ArrowUp",
  "ArrowLeft",
  "ArrowDown",
  "ArrowRight",
]);

export type InputIntent = Omit<ClientInput, "seq">;
export interface GameCallbacks {
  onInput: (input: InputIntent) => void;
  onTarget?: (id: string) => void;
  onInteract?: () => void;
  onSupply?: (kind?: SupplyKind) => void;
  onAbility?: (slot: number) => void;
  onNavigation?: (state: NavigationState) => void;
}
export interface JoystickState {
  active: boolean;
  x: number;
  y: number;
  dx: number;
  dy: number;
}
interface TargetTap extends Point {
  targetId: string;
  destination?: Point;
}

export class GameInput {
  readonly joystick: JoystickState = {
    active: false,
    x: 98,
    y: 0,
    dx: 0,
    dy: 0,
  };
  private readonly keys = new Set<string>();
  private readonly abort = new AbortController();
  private readonly timer: ReturnType<typeof setInterval>;
  private targetId = "";
  private mouseFollow: { pointerId: number; point: Point; navigateOnRelease: boolean } | null = null;
  private readonly targetTaps = new Map<number, TargetTap>();
  private readonly capturedPointers = new Set<number>();
  private joystickPointer: number | undefined;
  private buttonBoost = false;
  private suspended = false;
  private focused = !document.hidden;
  private lastDirection: Point = { x: 1, y: 0 };
  private disposed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly callbacks: GameCallbacks,
    private readonly toWorld: (screen: Point) => Point,
    private readonly pickTarget: (screen: Point) => string,
    private readonly localPosition: () => Point,
    private readonly maxMovementSpeed: () => number | null,
    private readonly navigation?: PointNavigation,
  ) {
    const options = { signal: this.abort.signal };
    if (!canvas.hasAttribute("tabindex")) canvas.tabIndex = 0;
    canvas.addEventListener("focus", this.focus, options);
    canvas.addEventListener("pointerdown", this.pointerDown, options);
    canvas.addEventListener("pointermove", this.pointerMove, options);
    canvas.addEventListener("pointerup", this.pointerUp, options);
    canvas.addEventListener("pointercancel", this.pointerCancel, options);
    canvas.addEventListener("lostpointercapture", this.pointerCancel, options);
    canvas.addEventListener(
      "contextmenu",
      (event) => event.preventDefault(),
      options,
    );
    window.addEventListener("keydown", this.keyDown, options);
    window.addEventListener("keyup", this.keyUp, options);
    window.addEventListener("blur", this.blur, options);
    window.addEventListener("focus", this.focus, options);
    window.addEventListener("pagehide", this.blur, options);
    window.addEventListener("pageshow", this.focus, options);
    window.addEventListener("resize", this.viewportChange, options);
    window.visualViewport?.addEventListener("resize", this.viewportChange, options);
    window.visualViewport?.addEventListener("scroll", this.viewportChange, options);
    document.addEventListener(
      "visibilitychange",
      this.visibilityChange,
      options,
    );
    this.timer = setInterval(this.sendInput, 50);
  }

  get selectedTarget(): string {
    return this.targetId;
  }
  get isTouchDevice(): boolean {
    return matchMedia("(pointer: coarse)").matches;
  }
  get navigationState(): NavigationState {
    return this.navigation?.state ?? { status: "idle", reason: null };
  }

  navigate(point: Point): void {
    if (
      this.disposed ||
      this.suspended ||
      !this.focused ||
      document.hidden ||
      this.mouseFollow !== null || this.keyboardOrJoystickHeld() ||
      !Number.isFinite(point.x) ||
      !Number.isFinite(point.y)
    )
      return;
    this.navigation?.navigate({ ...point });
    this.sendInput();
  }

  cancelNavigation(): void {
    this.navigation?.cancel("cancelled");
    for (const tap of this.targetTaps.values()) delete tap.destination;
    if (!this.disposed) this.sendInput();
  }

  selectTarget(id: string): void {
    if (this.targetId === id) return;
    this.targetId = id;
    this.callbacks.onTarget?.(id);
  }

  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
    if (suspended) {
      this.reset();
      this.selectTarget("");
    }
  }

  setControls(controls: { boost: boolean }): void {
    this.buttonBoost =
      !this.suspended && this.focused && !document.hidden && controls.boost;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    clearInterval(this.timer);
    this.abort.abort();
    this.reset();
  }

  private point(event: PointerEvent): Point {
    const bounds = this.canvas.getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  private readonly pointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    if (this.suspended || document.hidden) return;
    this.canvas.focus({ preventScroll: true });
    this.focused = true;
    const point = this.point(event);
    const joystickZone =
      event.pointerType !== "mouse" &&
      point.x < this.canvas.clientWidth * 0.42 &&
      point.y > this.canvas.clientHeight * 0.35;
    if (joystickZone && this.joystickPointer === undefined) {
      this.joystickPointer = event.pointerId;
      for (const tap of this.targetTaps.values()) delete tap.destination;
      Object.assign(this.joystick, {
        active: true,
        x: point.x,
        y: point.y,
        dx: 0,
        dy: 0,
      });
    } else {
      const targetId = this.pickTarget(point);
      if (event.pointerType === "mouse") {
        if (event.button !== 0) return;
        if (targetId) this.selectTarget(targetId);
        else {
          this.cancelManualNavigation();
          this.mouseFollow = { pointerId: event.pointerId, point,
            navigateOnRelease: !this.keyboardOrJoystickHeld() };
        }
      } else this.targetTaps.set(event.pointerId, {
        ...point,
        targetId,
        ...(!targetId &&
        !joystickZone &&
        !this.keyboardOrJoystickHeld()
          ? { destination: this.toWorld(point) }
          : {}),
      });
    }
    this.canvas.setPointerCapture(event.pointerId);
    this.capturedPointers.add(event.pointerId);
    if (event.pointerType === "mouse") this.sendInput();
  };

  private readonly pointerMove = (event: PointerEvent): void => {
    if (this.suspended || !this.focused || document.hidden) return;
    const point = this.point(event);
    if (this.joystickPointer === event.pointerId) {
      const movement = normalizeMovement(
        (point.x - this.joystick.x) / 52,
        (point.y - this.joystick.y) / 52,
      );
      this.joystick.dx = movement.x;
      this.joystick.dy = movement.y;
      if (movement.x !== 0 || movement.y !== 0) this.cancelManualNavigation();
    } else if (this.mouseFollow?.pointerId === event.pointerId) {
      if ((event.buttons & 1) === 0) {
        this.releasePointer(event.pointerId);
        this.sendInput();
      } else this.mouseFollow.point = point;
    }
    const tap = this.targetTaps.get(event.pointerId);
    if (tap && Math.hypot(point.x - tap.x, point.y - tap.y) >= 22)
      this.targetTaps.delete(event.pointerId);
  };

  private readonly pointerUp = (event: PointerEvent): void => {
    if (this.mouseFollow?.pointerId === event.pointerId && event.button === 0) {
      const destination = this.toWorld(this.point(event));
      const navigateOnRelease = this.mouseFollow.navigateOnRelease;
      this.releasePointer(event.pointerId);
      if (navigateOnRelease) this.navigate(destination);
      else this.sendInput();
      return;
    }
    if (event.pointerId === this.joystickPointer) this.releaseJoystick();
    const tap = this.targetTaps.get(event.pointerId);
    if (tap) {
      const point = this.point(event);
      if (Math.hypot(point.x - tap.x, point.y - tap.y) < 22) {
        if (event.pointerType !== "mouse") this.selectTarget(tap.targetId);
        if (tap.destination) this.navigate(tap.destination);
      }
    }
    if (this.releasePointer(event.pointerId)) this.sendInput();
  };

  private readonly pointerCancel = (event: PointerEvent): void => {
    if (event.pointerId === this.joystickPointer) this.releaseJoystick();
    if (this.releasePointer(event.pointerId)) this.sendInput();
  };

  private readonly keyDown = (event: KeyboardEvent): void => {
    if (this.suspended || document.hidden || event.defaultPrevented) return;
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement
    )
      return;
    const abilityIndex = abilityShortcutIndex(event.code);
    if (!this.focused) {
      const gameKey = movementKeys.has(event.code) || abilityIndex !== null ||
        ["Space", "ShiftLeft", "KeyR", "KeyF", "KeyT", "KeyE", "Escape"].includes(event.code);
      if (!event.isTrusted || event.repeat || !gameKey) return;
      this.focused = true;
    }
    if (
      [
        "KeyW",
        "KeyA",
        "KeyS",
        "KeyD",
        "ArrowUp",
        "ArrowLeft",
        "ArrowDown",
        "ArrowRight",
        "Space",
      ].includes(event.code)
    )
      event.preventDefault();
    this.keys.add(event.code);
    if (movementKeys.has(event.code)) this.cancelManualNavigation();
    if (event.repeat) return;
    if (event.code === "KeyR") this.callbacks.onSupply?.("battery");
    if (event.code === "KeyF") this.callbacks.onSupply?.("rocket");
    if (event.code === "KeyT") this.callbacks.onSupply?.("drone");
    if (event.code === "KeyE") this.callbacks.onInteract?.();
    if (abilityIndex !== null) {
      event.preventDefault();
      this.callbacks.onAbility?.(abilityIndex);
    }
    if (event.code === "Escape") {
      this.selectTarget("");
      if (this.mouseFollow) this.releasePointer(this.mouseFollow.pointerId);
      this.cancelNavigation();
    }
  };

  private readonly keyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };
  private readonly visibilityChange = (): void => {
    if (document.hidden) this.blur();
    else this.focused = document.hasFocus();
  };
  private readonly blur = (): void => {
    this.focused = false;
    this.reset();
  };
  private readonly focus = (): void => {
    this.focused = !document.hidden;
  };
  private readonly reset = (): void => {
    this.navigation?.cancel("unavailable");
    this.keys.clear();
    this.resetPointers();
    this.callbacks.onInput(neutralFlightInput(this.localPosition()));
  };
  private readonly viewportChange = (): void => {
    this.navigation?.cancel("unavailable");
    this.resetPointers();
    this.sendInput();
  };

  private resetPointers(): void {
    this.mouseFollow = null;
    this.releaseJoystick();
    this.targetTaps.clear();
    for (const pointerId of this.capturedPointers)
      this.releasePointer(pointerId);
    this.buttonBoost = false;
  }

  private releasePointer(pointerId: number): boolean {
    const following = this.mouseFollow?.pointerId === pointerId;
    if (following) this.mouseFollow = null;
    this.targetTaps.delete(pointerId);
    this.capturedPointers.delete(pointerId);
    if (this.canvas.hasPointerCapture(pointerId))
      this.canvas.releasePointerCapture(pointerId);
    return following;
  }

  private releaseJoystick(): void {
    this.joystickPointer = undefined;
    Object.assign(this.joystick, { active: false, dx: 0, dy: 0 });
  }

  private keyboardOrJoystickHeld(): boolean {
    return (
      this.joystickPointer !== undefined ||
      [...this.keys].some((key) => movementKeys.has(key))
    );
  }

  private cancelManualNavigation(): void {
    this.navigation?.cancel("manual");
    if (this.mouseFollow) this.mouseFollow.navigateOnRelease = false;
    for (const tap of this.targetTaps.values()) delete tap.destination;
  }

  readIntent(): InputIntent {
    if (this.disposed || this.suspended || !this.focused || document.hidden)
      return neutralFlightInput(this.localPosition());
    const keyX =
      Number(this.keys.has("KeyD") || this.keys.has("ArrowRight")) -
      Number(this.keys.has("KeyA") || this.keys.has("ArrowLeft"));
    const keyY =
      Number(this.keys.has("KeyS") || this.keys.has("ArrowDown")) -
      Number(this.keys.has("KeyW") || this.keys.has("ArrowUp"));
    const automatic = this.navigation?.steer();
    const position = this.localPosition();
    const mouseTarget = this.mouseFollow ? this.toWorld(this.mouseFollow.point) : null;
    const directional = this.keyboardOrJoystickHeld();
    const movement =
      automatic?.movement ??
      (mouseTarget && !directional
        ? mouseFollowMovement(position, mouseTarget, this.maxMovementSpeed())
        : normalizeMovement(keyX + this.joystick.dx, keyY + this.joystick.dy));
    if (Math.hypot(movement.x, movement.y) > 0.05)
      this.lastDirection = movement;
    const aim =
      automatic?.aim ??
      (mouseTarget && !directional
        ? mouseTarget
        : {
            x: position.x + this.lastDirection.x * 1000,
            y: position.y + this.lastDirection.y * 1000,
          });
    const input: InputIntent = {
      mx: movement.x,
      my: movement.y,
      targetId: this.targetId,
      fire: true,
      boost:
        this.buttonBoost ||
        this.keys.has("Space") ||
        this.keys.has("ShiftLeft"),
      aimX: aim.x,
      aimY: aim.y,
    };
    return input;
  }

  private readonly sendInput = (): void => {
    const intent = this.readIntent();
    if (import.meta.env.DEV) {
      const data = this.canvas.dataset;
      data.inputMx = String(intent.mx);
      data.inputMy = String(intent.my);
      data.inputSuspended = String(this.suspended);
      data.inputFocused = String(this.focused);
      data.inputKeys = [...this.keys].filter(key => movementKeys.has(key)).join(",");
      data.inputMouse = this.mouseFollow ? "held" : "released";
      data.inputMaxSpeed = String(this.maxMovementSpeed());
      data.inputSampleAt = String(performance.now());
    }
    this.callbacks.onInput(intent);
  };
}
